import {
    centsToFormattedDollars,
    formatExecutionTime,
    formatWithoutTimezone,
    getRecurringTransferEndText,
} from '@fruitful/fruitils';
import type { HolisticSummary } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import React, { useMemo, useRef, useState } from 'react';
import {
    BodyMediumRegular,
    H5Regular,
    Icon,
    Spinner,
    TrashIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import useCancelPaymentSchedule from '../../../../banking/banking/hooks/useCancelPaymentSchedule';
import useGetPaymentSchedules from '../../../../banking/banking/hooks/useGetPaymentSchedules';
import { ConfirmModal } from '../../../../common/components/ConfirmModal';
import { adminUserAtom, selectedMembershipAtom } from '../../../entityAtoms';
import type { MemberDetails, MembershipDetails } from '../../../entityTypes';

interface MemberRecurringTransfersProps {
    user: MemberDetails;
}

const DEFAULT_PADDING = 8;

export const MembershipRecurringTransfers = ({
    membershipDetails,
}: {
    membershipDetails: MembershipDetails;
}) => {
    return (
        <XStack justifyContent="space-between" flexWrap="wrap">
            {membershipDetails.users.map((member) => (
                <MemberRecurringTransfers
                    key={`${member.id}:transfers`}
                    user={member}
                />
            ))}
        </XStack>
    );
};

const hasBankAccounts = (accounts?: HolisticSummary) => {
    const ffAccounts =
        accounts != null
            ? [
                  accounts.spend.accounts.filter(
                      (a) => a.bankAccountId != null,
                  ),
                  accounts.save.accounts.filter((a) => a.bankAccountId != null),
                  accounts.invest.accounts.filter(
                      (a) => a.bankAccountId != null,
                  ),
                  accounts.borrow.accounts.filter(
                      (a) => a.bankAccountId != null,
                  ),
              ]
                  .flat()
                  .filter((a) => a != null)
            : [];

    return ffAccounts.length > 0;
};

export const MemberRecurringTransfers = ({
    user,
}: MemberRecurringTransfersProps) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const selectedMembership = useAtomValue(selectedMembershipAtom);
    const adminUser = useAtomValue(adminUserAtom);

    const fetchTransfers = hasBankAccounts(selectedMembership?.holisticSummary);

    const transferRef = useRef<string | undefined>(undefined);

    const {
        data: recurringTransfers,
        isLoading,
        isRefetching,
        refetch,
    } = useGetPaymentSchedules(
        {
            syncteraPersonId: user.syncteraPersonId,
        },
        {
            enabled: user.syncteraPersonId != null && fetchTransfers,
        },
    );

    const {
        mutateAsync: cancelPaymentSchedule,
        isPending: isDeletingTransfer,
    } = useCancelPaymentSchedule();

    const cancelRecurringTransfer = async (paymentScheduleId: string) => {
        return await cancelPaymentSchedule(
            {
                urlParams: {
                    paymentScheduleId,
                },
            },
            {
                onSettled: () => {
                    void refetch();
                },
            },
        );
    };

    const makeRow = (left: string, right?: string) => {
        return (
            <>
                <BodyMediumRegular flexBasis="50%">{left}</BodyMediumRegular>
                <BodyMediumRegular flexBasis="50%" textAlign="right">
                    {right ?? ''}
                </BodyMediumRegular>
            </>
        );
    };

    const membershipPermissions = adminUser.authorization.selectedMembership;
    const canCancel = membershipPermissions.bankOps.canUse;

    const isWaiting = isLoading || isDeletingTransfer || isRefetching;
    const hasRecurringTransfers = (recurringTransfers?.length ?? 0) > 0;
    const prefix =
        isLoading || isRefetching
            ? 'Fetching'
            : hasRecurringTransfers
              ? 'Recurring'
              : 'No recurring';
    const headerText = `${prefix} transfers for ${user.firstName}`;

    const transfers = useMemo(() => {
        return (
            recurringTransfers?.map((transfer) => {
                return (
                    <YStack
                        key={transfer.id}
                        backgroundColor="$gray300"
                        padding={8}
                        paddingLeft={0}
                        borderWidth={1}
                    >
                        <XStack padding={DEFAULT_PADDING}>
                            {makeRow('id: ', transfer.id)}
                            {canCancel && (
                                <View
                                    padding={2}
                                    onPress={() => {
                                        transferRef.current = transfer.id;
                                        setShowConfirm(true);
                                    }}
                                >
                                    <Icon
                                        style={{ cursor: 'pointer' }}
                                        color="$red1000"
                                        icon={TrashIcon}
                                    />
                                </View>
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={1}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                        >
                            {makeRow(
                                'Description: ',
                                transfer.recurringDescription === ''
                                    ? '(no member description set)'
                                    : transfer.recurringDescription,
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={1}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                            paddingBottom={2}
                        >
                            {makeRow(
                                'From:',
                                `${transfer.fromAccount.nickname} (*${transfer.fromAccount.accountNumberMasked})`,
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={0}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                            paddingTop={2}
                        >
                            {makeRow(
                                'To:',
                                `${transfer.toAccount.nickname} (*${transfer.toAccount.accountNumberMasked})`,
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={1}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                        >
                            {makeRow(
                                'Next payment date:',
                                transfer.nextPaymentDate?.scheduled_date,
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={1}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                        >
                            {makeRow(
                                'Execution time:',
                                formatExecutionTime(
                                    transfer.executionTime ?? '11:00:00',
                                ),
                            )}
                        </XStack>
                        <XStack
                            borderTopWidth={1}
                            borderBottomWidth={0}
                            padding={DEFAULT_PADDING}
                        >
                            {makeRow(
                                'Ends: ',
                                getRecurringTransferEndText(
                                    transfer.recurringEndType,
                                    transfer.recurringEndDate != null
                                        ? formatWithoutTimezone(
                                              transfer.recurringEndDate,
                                              'MMM dd, yyyy',
                                          )
                                        : undefined,
                                    transfer.recurringCount,
                                ),
                            )}
                        </XStack>
                        <XStack borderTopWidth={1} padding={DEFAULT_PADDING}>
                            {makeRow(
                                'Amount:',
                                centsToFormattedDollars(transfer.amount),
                            )}
                        </XStack>
                        <XStack borderTopWidth={1} padding={DEFAULT_PADDING}>
                            {makeRow(
                                'Income split applied:',
                                (
                                    transfer.metadata?.applyIncomeSplit ?? false
                                ).toString(),
                            )}
                        </XStack>
                    </YStack>
                );
            }) ?? []
        );
    }, [recurringTransfers]);

    return (
        <YStack flex={1} paddingTop={16}>
            <H5Regular>{headerText}</H5Regular>
            {isWaiting ? (
                <Spinner />
            ) : (
                <YStack padding={8} paddingLeft={0} gap={16} flex={1}>
                    {transfers}
                    <ConfirmModal
                        isOpen={showConfirm && transferRef.current != null}
                        onClose={() => {
                            setShowConfirm(false);
                        }}
                        onConfirm={() => {
                            if (transferRef.current != null) {
                                void cancelRecurringTransfer(
                                    transferRef.current,
                                );
                                transferRef.current = undefined;
                            }
                            setShowConfirm(false);
                        }}
                    />
                </YStack>
            )}
        </YStack>
    );
};
